import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { getTestDetailsService } from '@/api/services/testService'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card'
import { Skeleton } from '@/components/skeleton'
import PageWrap from '@/components/layout/containers/PageWrap'
import { Seo } from '@/components/specific/seo/Seo'
import { Button } from '@/components/ui/common/Button'

function TestDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['test', id],
    queryFn: async () => {
      if (!id) return null
      const response = await getTestDetailsService(id)
      if (!response.success) {
        console.error('Błąd API:', response.data)
        return null
      }
      return response.data
    }
  })

  const handleStartTest = () => {
    if (data?.modules&& data?.modules.length > 0) {
      navigate(`/tests/${id}/session`)
    }
  }

  return (
    <>
      <Seo />
      <PageWrap>
        <div className="p-6">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-1/3 mb-4" />
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : error ? (
            <div className="text-red-500">Wystąpił błąd podczas ładowania szczegółów testu</div>
          ) : data ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold mb-4">{data.purpose || 'Test bez tytułu'}</h1>
                <div className="space-y-2 text-muted-foreground">
                  {data.constraints && (
                    <p><span className="font-semibold">Ograniczenia:</span> {data.constraints}</p>
                  )}
                  {data.procedure && (
                    <p><span className="font-semibold">Procedura:</span> {data.procedure}</p>
                  )}
                  {data.recommendations && (
                    <p><span className="font-semibold">Rekomendacje:</span> {data.recommendations}</p>
                  )}
                  <p><span className="font-semibold">Utworzono:</span> {new Date(data.createdAt).toLocaleDateString()}</p>
                  {data.updatedAt && (
                    <p><span className="font-semibold">Zaktualizowano:</span> {new Date(data.updatedAt).toLocaleDateString()}</p>
                  )}
                </div>
              </div>

              {data.modules.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-4">Moduły testu</h2>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {data.modules.map((module) => (
                      <Card key={module.id}>
                        <CardHeader>
                          <CardTitle>{module.name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">
                            Liczba pytań: {module.questionCount}
                          </p>
                          {module.text && (
                            <p className="text-sm text-muted-foreground mt-2">
                              {module.text}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-center mt-8">
                <Button onClick={handleStartTest} size="lg">
                  Rozpocznij test
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-red-500">Nie znaleziono testu</div>
          )}
        </div>
      </PageWrap>
    </>
  )
}

export default TestDetails 